import React from 'react';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { Box, Typography } from '@mui/material';

const EVENT_TYPES = {
    STRIKE: { value: 1, label: 'Strike', color: '#ffeb3b' }, // Yellow
    BALL: { value: 2, label: 'Ball', color: '#4caf50' },   // Green
    HIT: { value: 3, label: 'Hit', color: '#9c27b0' },     // Purple
    OUT: { value: 0, label: 'Out', color: '#f44336' },     // Red
    WALK: { value: 4, label: 'FB', color: '#2196f3' },     // Blue (FB = Walk)
    RUN: { value: 5, label: 'Run', color: '#ff9800' },     // Orange
    OTHER: { value: -1, label: 'Other', color: '#9e9e9e' }
};

const Y_AXIS_EVENTS = [
    EVENT_TYPES.OUT,
    EVENT_TYPES.STRIKE,
    EVENT_TYPES.BALL,
    EVENT_TYPES.HIT,
    EVENT_TYPES.WALK,
    EVENT_TYPES.RUN,
];

const Y_AXIS_TICKS = Y_AXIS_EVENTS.map((eventType) => eventType.value);
const Y_AXIS_LABELS = Object.fromEntries(
    Y_AXIS_EVENTS.map((eventType) => [eventType.value, eventType.label])
);

const formatYAxisTick = (value) => Y_AXIS_LABELS[value] || '';

const SequenceChart = ({ history, current }) => {
    const fullHistory = React.useMemo(() => {
        if (!history) return [];
        return current ? [...history, current] : history;
    }, [history, current]);

    if (fullHistory.length < 2) {
        return (
            <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">No Data</Typography>
            </Box>
        );
    }

    const processData = (historyData) => {
        const data = [];

        // Helper to determine event type between two states
        const getAction = (prev, curr) => {
            // Prioritize logic based on what likely happened
            if (curr.hits > prev.hits) return EVENT_TYPES.HIT;
            // Ignore K (Strikeout) as requested
            // if (curr.strikeouts > prev.strikeouts) return { ...EVENT_TYPES.OUT, label: 'K' };
            if (curr.walks > prev.walks) return EVENT_TYPES.WALK;
            if (curr.runsAllowed > prev.runsAllowed) return EVENT_TYPES.RUN;

            if (curr.strikes > prev.strikes) return EVENT_TYPES.STRIKE;
            if (curr.balls > prev.balls) return EVENT_TYPES.BALL;
            if (curr.outs > prev.outs) return EVENT_TYPES.OUT;

            return EVENT_TYPES.OTHER;
        };

        historyData.reduce((prev, curr, index) => {
            if (index === 0) return curr;

            const eventType = getAction(prev, curr);

            // X-Axis Logic:
            // - If it's a Pitch (Strike/Ball), X = pitch count.
            // - If it's a Result (Hit/Out/Walk), we want it on the SAME X as the pitch that caused it.
            //   In our history, Hit/Out/Walk usually happen AFTER the pitch count increment if handled strictly (or at same time).
            //   However, our `App.js` logic:
            //   - handleStrike: +1 Strikes, +1 Pitches, saveHistory. Result: New entry.
            //     Change: Strikes increase, Pitches increase. Event: Strike. X = new pitch count.
            //   - handleHit: +1 Hits, +0 Pitches (in default App.js), saveHistory. Result: New entry.
            //     Change: Hits increase, Pitches SAME. Event: Hit. X = same pitch count.
            //   - handleOut: +1 Outs, +0 Pitches (in default App.js), saveHistory. Result: New entry.
            //     Change: Outs increase, Pitches SAME. Event: Out. X = same pitch count.
            //   - handleWalk: +1 Walks, +0 Pitches (in default App.js), saveHistory. Result: New entry.
            //     Change: Walks increase, Pitches SAME. Event: Walk. X = same pitch count.
            //   - handleBall: +1 Balls, +1 Pitches, saveHistory. Result: New entry.
            //     Change: Balls increase, Pitches increase.

            // So, simply using `curr.pitches` as X should satisfy "same pitch count".
            // If prev was a Strike (count 4), and now Hit (count 4), both plotted at 4.
            // Recharts Scatter can plot multiple points at same X.

            const pitchCount = curr.pitches;

            if (eventType !== EVENT_TYPES.OTHER) {
                data.push({
                    x: pitchCount,
                    y: eventType.value,
                    type: eventType.label,
                    color: eventType.color,
                    payload: curr
                });
            }
            return curr;
        });

        return data;
    };

    const data = processData(fullHistory);

    return (
        <Box sx={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
                <ScatterChart
                    margin={{
                        top: 20,
                        right: 20,
                        bottom: 20,
                        left: 20,
                    }}
                >
                    <CartesianGrid />
                    <XAxis type="number" dataKey="x" name="Sequence" unit="" interval={0} tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis
                        type="number"
                        dataKey="y"
                        name="Type"
                        ticks={Y_AXIS_TICKS}
                        interval={0}
                        width={70}
                        tick={{ fontSize: 12 }}
                        tickFormatter={formatYAxisTick}
                        domain={[-0.5, 5.5]} // Pad top and bottom
                    />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                                <Box sx={{ bgcolor: 'white', p: 1, border: '1px solid #ccc' }}>
                                    <Typography variant="body2">{`#${data.x}: ${data.type}`}</Typography>
                                </Box>
                            );
                        }
                        return null;
                    }} />
                    <Scatter name="Events" data={data}>
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Scatter>
                </ScatterChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default SequenceChart;
